import { AnyParameter } from '../types/SonForm';

type ParamByName<TParam, TName> = Extract<TParam, { name: TName }>;
type ParamByType<TParam, TType> = Extract<TParam, { type: TType }>;

export function byName<TName extends string>(name: TName) {
  return <TParams extends AnyParameter>(param: TParams): param is ParamByName<TParams, TName> => param.name === name;
}

export function byType<TType extends string>(type: TType) {
  return <TParams extends AnyParameter>(param: TParams): param is ParamByType<TParams, TType> => param.type === type;
}

/**
 * Param utilities to query by type or by name.
 * */
export function formParamsUtils<TParam extends AnyParameter>({ params }: { params: ReadonlyArray<TParam> }) {
  return {
    /**
     * find a parameter by its name.
     */
    findByName<TName extends TParam['name']>(name: TName) {
      return params.find(byName(name));
    },
    /**
     * get a parameter by its name or throw if no parameter is found.
     */
    getByName<TName extends TParam['name']>(name: TName) {
      const param = params.find(byName(name));
      if (!param) {
        throw Error(`parameter with name ${name} is missing!`);
      }
      return param;
    },
    getAllByName<TName extends TParam['name']>(name: TName) {
      return params.filter(byName(name));
    },
    getAllByType<TType extends TParam['type']>(type: TType) {
      return params.filter(byType(type));
    },
    some(predicate: (value: TParam, index: number, array: readonly TParam[]) => unknown, thisArg?: any) {
      return params.some(predicate, thisArg);
    },
    anyParameters() {
      return params && params.length > 0;
    },
  };
}
